import { Injectable } from "@angular/core";
import { BehaviorSubject, Observable, Subject } from "rxjs";

/**
 * DynaDimmerStateService
 * --------------------------------------------------------------------
 * Centralises all DynaDimmer-feature reactive state previously stored
 * on `ApiService`.
 */

type DynaPrimitiveState = string | number | boolean | null | undefined;
type DynaObjectState = Record<string, unknown>;
type DynaCollectionState = unknown[];
type DynaStateValue = DynaPrimitiveState | DynaObjectState | DynaCollectionState;

@Injectable({ providedIn: "root" })
export class DynaDimmerStateService {
  // --- Misc dyna text & numeric values ------------------------------
  readonly dynaTimeHours = new BehaviorSubject<DynaStateValue>("");
  readonly dynaOutputLevel = new BehaviorSubject<DynaStateValue>("");
  readonly dynaFadeTimess = new BehaviorSubject<DynaStateValue>("");

  readonly dynaDimmerEnabledMode = new BehaviorSubject<DynaStateValue>(false);
  readonly getDynaDimmerEnabledMode: Observable<DynaStateValue> =
    this.dynaDimmerEnabledMode.asObservable();

  readonly dynaMidPointShiftMode = new BehaviorSubject<DynaStateValue>(true);
  readonly getDynaMidPointShiftMode: Observable<DynaStateValue> =
    this.dynaMidPointShiftMode.asObservable();

  readonly dynaDimmerEdited = new BehaviorSubject<DynaStateValue>(false);

  readonly dynaMidPointShiftValue = new BehaviorSubject<DynaStateValue>(0);
  readonly getDynaMidPointShiftValue: Observable<DynaStateValue> =
    this.dynaMidPointShiftValue.asObservable();

  readonly dynaMIdPointShiftDefaultProperties = new BehaviorSubject<DynaCollectionState>([]);
  readonly getDynaMidPointSHiftDefaultProperties: Observable<DynaCollectionState> =
    this.dynaMIdPointShiftDefaultProperties.asObservable();

  readonly dynaDefaultValues = new BehaviorSubject<DynaCollectionState>([]);

  readonly dynaOverrideMode = new BehaviorSubject<DynaStateValue>(false);
  readonly getDynaOverrideMode: Observable<DynaStateValue> = this.dynaOverrideMode.asObservable();

  readonly dynaTimeReferencedMode = new BehaviorSubject<DynaStateValue>(false);
  readonly getDynaTimeReferencedMode: Observable<DynaStateValue> =
    this.dynaTimeReferencedMode.asObservable();

  readonly dynadefaultOverrideMode = new BehaviorSubject<DynaStateValue>(false);
  readonly getDefaultDynaOverrideMode: Observable<DynaStateValue> =
    this.dynadefaultOverrideMode.asObservable();

  readonly dynaDefaultTimeReferencedMode = new BehaviorSubject<DynaStateValue>(false);
  readonly getDefaultDynaTimeReferencedMode: Observable<DynaStateValue> =
    this.dynaDefaultTimeReferencedMode.asObservable();

  readonly dynaDefaultDimmerEnabledMode = new BehaviorSubject<DynaStateValue>(false);
  readonly getDefaultDynaDimmerEnabledMode: Observable<DynaStateValue> =
    this.dynaDefaultDimmerEnabledMode.asObservable();

  // Per-scene toggles (1..5)
  readonly dynaDimmerScene1 = new BehaviorSubject<DynaStateValue>(true);
  readonly dynaDimmerScene2 = new BehaviorSubject<DynaStateValue>(false);
  readonly dynaDimmerScene3 = new BehaviorSubject<DynaStateValue>(false);
  readonly dynaDimmerScene4 = new BehaviorSubject<DynaStateValue>(false);
  readonly dynaDimmerScene5 = new BehaviorSubject<DynaStateValue>(false);

  readonly dynaDimmerAllScenes = new BehaviorSubject<DynaCollectionState>([]);
  readonly getDynaDimmerAllScenes: Observable<DynaCollectionState> =
    this.dynaDimmerAllScenes.asObservable();

  readonly dynaDependencyProp = new BehaviorSubject<DynaStateValue>(false);
  readonly getDynaDependencyProp: Observable<DynaStateValue> = this.dynaDependencyProp.asObservable();

  readonly dynaShowEnabled = new BehaviorSubject<DynaStateValue>(false);
  readonly getDynaShowEnabled: Observable<DynaStateValue> = this.dynaShowEnabled.asObservable();

  readonly dynaShowOverrideEnabled = new BehaviorSubject<DynaStateValue>(false);
  readonly getDynaShowOverrideEnabled: Observable<DynaStateValue> =
    this.dynaShowOverrideEnabled.asObservable();

  readonly dynaShowTimeReference = new BehaviorSubject<DynaStateValue>(false);
  readonly getDynaShowTimeReference: Observable<DynaStateValue> =
    this.dynaShowTimeReference.asObservable();

  readonly finalDynaDimmerData = new BehaviorSubject<DynaStateValue>("");
  readonly getFinalDynaDimmerData: Observable<DynaStateValue> = this.finalDynaDimmerData.asObservable();

  readonly isDynaError = new BehaviorSubject<DynaStateValue>(false);
  readonly getIsDynaError: Observable<DynaStateValue> = this.isDynaError.asObservable();

  // Imperative one-shot signal: notifies subscribers a user interacted
  // with a dyna widget (no carried value).
  private readonly dynaUserInteracted = new Subject<void>();
  readonly getDynaUserInteracted: Observable<void> = this.dynaUserInteracted.asObservable();

  readonly dynaDimmerText = new BehaviorSubject<DynaStateValue>("");

  // --- Setters (same names as the original ApiService) ---------------
  sendDynaTimeHours(data: DynaStateValue): void {
    this.dynaTimeHours.next(data);
  }
  sendDynaOutputLevel(data: DynaStateValue): void {
    this.dynaOutputLevel.next(data);
  }
  sendDynaFadeTimess(data: DynaStateValue): void {
    this.dynaFadeTimess.next(data);
  }

  sendDynaDimmerEnabledMode(data: DynaStateValue): void {
    this.dynaDimmerEnabledMode.next(data);
  }
  sendDynaMidPointShiftMode(data: DynaStateValue): void {
    this.dynaMidPointShiftMode.next(data);
  }

  setDynaDimmerEdited(data: DynaStateValue): void {
    this.dynaDimmerEdited.next(data);
  }
  sendDynaMidPointShiftValue(data: DynaStateValue): void {
    this.dynaMidPointShiftValue.next(data);
  }
  setDynaMidPointShiftDefaultsSchemaProps(data: DynaCollectionState): void {
    this.dynaMIdPointShiftDefaultProperties.next(data);
  }
  sendDynaDefaultValues(data: DynaCollectionState): void {
    this.dynaDefaultValues.next(data);
  }

  sendDynaOveerideMode(data: DynaStateValue): void {
    this.dynaOverrideMode.next(data);
  }
  sendDynaTimeReferenceMode(data: DynaStateValue): void {
    this.dynaTimeReferencedMode.next(data);
  }

  setDyanEnabledDefault(data: DynaStateValue): void {
    this.dynaDefaultDimmerEnabledMode.next(data);
  }
  setDyanTimeRefDefault(data: DynaStateValue): void {
    this.dynaDefaultTimeReferencedMode.next(data);
  }
  setDyanOverrideDefault(data: DynaStateValue): void {
    this.dynadefaultOverrideMode.next(data);
  }

  setDynaDimmerAllScenes(data: DynaCollectionState): void {
    this.dynaDimmerAllScenes.next(data);
  }
  sendDynaScene1(data: DynaStateValue): void {
    this.dynaDimmerScene1.next(data);
  }
  sendDynaScene2(data: DynaStateValue): void {
    this.dynaDimmerScene2.next(data);
  }
  sendDynaScene3(data: DynaStateValue): void {
    this.dynaDimmerScene3.next(data);
  }
  sendDynaScene4(data: DynaStateValue): void {
    this.dynaDimmerScene4.next(data);
  }
  sendDynaScene5(data: DynaStateValue): void {
    this.dynaDimmerScene5.next(data);
  }

  setDynaDimmerInterDependency(dependency: DynaStateValue): void {
    this.dynaDependencyProp.next(dependency);
  }
  showDynaEnabled(data: DynaStateValue): void {
    this.dynaShowEnabled.next(data);
  }
  showDynaOverrideEnabled(data: DynaStateValue): void {
    this.dynaShowOverrideEnabled.next(data);
  }
  showDynaTimeRefernece(data: DynaStateValue): void {
    this.dynaShowTimeReference.next(data);
  }

  sendisDynaValid(data: DynaStateValue): void {
    this.isDynaError.next(data);
  }
  sendDynaDimmerText(data: DynaStateValue): void {
    this.dynaDimmerText.next(data);
  }

  emitDynaUserInteracted(): void {
    this.dynaUserInteracted.next();
  }

  setFinalDynaDimmerData(data: DynaStateValue): void {
    this.finalDynaDimmerData.next(data);
  }
}
